import { ArrowLeft, ShoppingBag, Tag, Wallet } from 'lucide-react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import { Dialog, DialogContent, DialogTitle } from '@/shared/widgets/dialog';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';
import { calculateGmsPlanBreakdown } from '../../utils/gmsPlan.utils';

export interface GoldMinePlanSheetProps {
  open: boolean;
  discountedPrice: number;
  gmsFirstInstallmentDiscountEnabled: boolean;
  gmsFirstInstallmentDiscountPercentage: number;
  onClose: () => void;
  onSubscribeClick?: () => void;
  onTncClick?: () => void;
}

const TIMELINE_STEPS = [
  { icon: Wallet, label: 'Pay', sublabel: 'Monthly' },
  { icon: Tag, label: 'Get Special', sublabel: 'Discounts' },
  { icon: ShoppingBag, label: 'Redeem &', sublabel: 'Purchase' },
] as const;

interface GoldMinePlanContentProps {
  discountedPrice: number;
  gmsFirstInstallmentDiscountEnabled: boolean;
  gmsFirstInstallmentDiscountPercentage: number;
  onClose: () => void;
  onSubscribeClick?: () => void;
  onTncClick?: () => void;
  TitleComponent: typeof SheetTitle | typeof DialogTitle;
}

function GoldMinePlanContent({
  discountedPrice,
  gmsFirstInstallmentDiscountEnabled,
  gmsFirstInstallmentDiscountPercentage,
  onClose,
  onSubscribeClick,
  onTncClick,
  TitleComponent,
}: GoldMinePlanContentProps) {
  const breakdown = calculateGmsPlanBreakdown(
    discountedPrice,
    gmsFirstInstallmentDiscountEnabled,
    gmsFirstInstallmentDiscountPercentage
  );

  const {
    productValue,
    installment,
    firstInstDiscount,
    totalPayableGMSAmount,
    totalDiscountPercentage,
  } = breakdown;

  return (
    <>
      <div className="flex shrink-0 items-center border-b border-[#e2e2e2] bg-white px-1 py-3">
        <button
          type="button"
          aria-label="Close Gold Mine plan details"
          className="p-3 text-[var(--link-blue)]"
          onClick={onClose}
        >
          <ArrowLeft className="h-[22px] w-[22px]" />
        </button>
        <TitleComponent className="text-[18px] font-semibold leading-none text-black">
          Gold Mine 10 + 1 Plan
        </TitleComponent>
      </div>

      <div className="overflow-y-auto bg-[#f7f7f7]">
        <div className="px-2.5 pt-4">
          <ul className="flex w-full">
            {TIMELINE_STEPS.map((step, index) => (
              <li key={step.label} className="relative flex-1 text-center">
                <div className="relative pb-1">
                  {index < TIMELINE_STEPS.length - 1 && (
                    <span
                      className="absolute left-1/2 top-[18px] z-0 h-px w-full border-t border-dashed border-[#979797]"
                      aria-hidden
                    />
                  )}
                  <div className="relative z-10 flex flex-col items-center">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#376dab] text-white">
                      <step.icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span className="absolute -translate-x-[90%] -translate-y-[10%] rounded-full bg-[#376dab] px-1.5 py-0.5 text-[8px] text-white">
                      {index + 1}
                    </span>
                  </div>
                </div>
                <div className="text-[11px] font-semibold leading-snug text-[#4a4a4a]">
                  {step.label}
                  <br />
                  {step.sublabel}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mx-2.5 mt-4 rounded border border-[#e2e2e2] bg-white px-3 py-3">
          <div className="flex justify-between pb-2.5 text-sm font-semibold text-[#4a4a4a]">
            Product Value
            <span>{formatDisplayPrice(productValue)}</span>
          </div>

          <div className="rounded border border-[#f38294] bg-[#fff1f3] px-2 py-2 text-sm font-semibold text-[#4a4a4a]">
            Recommended Monthly Amount:
            <span className="float-right">{formatDisplayPrice(installment)}</span>
          </div>

          <div className="mt-2.5 pb-1 text-sm font-semibold text-[#4a4a4a]">
            <div className="flex justify-between">
              Your total payment
              <span>{formatDisplayPrice(installment * 10)}</span>
            </div>
            <p className="mt-1 text-[11px] font-normal">
              Period of 10 months{' '}
              <span className="float-right">
                {formatDisplayPrice(installment)} x 10
              </span>
            </p>
          </div>

          {gmsFirstInstallmentDiscountEnabled && gmsFirstInstallmentDiscountPercentage > 0 && (
            <div className="mt-2.5 border-b border-[rgba(151,151,151,0.4)] pb-2.5 text-sm font-semibold">
              <div className="flex justify-between text-[#f15d4c]">
                Special Discount on 1st installment
                <span>{formatDisplayPrice(firstInstDiscount)}</span>
              </div>
              <p className="mt-1 text-[11px] font-normal text-[#4a4a4a]">
                {gmsFirstInstallmentDiscountPercentage}% off on 1st installment
              </p>
            </div>
          )}

          <div className="mt-2.5 border-b border-[rgba(151,151,151,0.4)] pb-2.5 text-sm font-semibold">
            <div className="flex justify-between text-[#f15d4c]">
              100% Discount on 11th installment
              <span>{formatDisplayPrice(installment)}</span>
            </div>
            <p className="mt-1 text-[11px] font-normal text-[#4a4a4a]">
              100% of 1 month installment value
            </p>
          </div>

          <div className="mt-2.5 pb-1 text-sm font-semibold text-[#4a4a4a]">
            <div className="flex justify-between">
              Buy any jewellery worth
              <span>{formatDisplayPrice(installment * 11)}</span>
            </div>
            <p className="mt-1 text-[11px] font-normal">After 11th month</p>
          </div>

          <div className="mt-2.5 border-t border-[rgba(151,151,151,0.4)] pt-2.5 text-sm font-semibold">
            <div className="flex justify-between text-[#f15d4c]">
              You effectively pay
              <span>{formatDisplayPrice(totalPayableGMSAmount)}</span>
            </div>
            <p className="mt-1 text-[11px] font-normal text-[#4a4a4a]">
              <strong className="rounded bg-green-600 px-1.5 py-0.5 text-white">
                {totalDiscountPercentage}%
              </strong>{' '}
              discount!
            </p>
          </div>
        </div>

        <p className="px-5 pt-3 text-center text-[11px] leading-4 text-[#4a4a4a]">
          <span className="text-[#e52626]">Note: </span>
          You can purchase any jewellery using the accumulated amount after 11 months*{' '}
          <a
            href="/tnc.html#gold-mine-10+1-plan"
            className="text-[#347ab8] underline"
            onClick={() => onTncClick?.()}
          >
            T&amp;C
          </a>
        </p>

        <div className="px-2.5 pb-3 pt-2">
          <a
            href={`/goldmine.html?installment=${installment}`}
            className="block w-full rounded bg-[#f15d4c] py-3.5 text-center text-sm font-bold uppercase text-white no-underline"
            onClick={() => onSubscribeClick?.()}
          >
            Start Gold Mine Plan
          </a>
        </div>
      </div>
    </>
  );
}

export function GoldMinePlanSheet({
  open,
  discountedPrice,
  gmsFirstInstallmentDiscountEnabled,
  gmsFirstInstallmentDiscountPercentage,
  onClose,
  onSubscribeClick,
  onTncClick,
}: GoldMinePlanSheetProps) {
  const isDesktop = useIsDesktop();

  const contentProps = {
    discountedPrice,
    gmsFirstInstallmentDiscountEnabled,
    gmsFirstInstallmentDiscountPercentage,
    onClose,
    onSubscribeClick,
    onTncClick,
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
        <DialogContent
          showCloseButton={false}
          className="flex max-h-[90vh] w-[calc(100%-2rem)] max-w-[520px] flex-col gap-0 overflow-hidden p-0"
        >
          <GoldMinePlanContent {...contentProps} TitleComponent={DialogTitle} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex h-auto max-h-[90dvh] flex-col gap-0 overflow-hidden p-0"
      >
        <GoldMinePlanContent {...contentProps} TitleComponent={SheetTitle} />
      </SheetContent>
    </Sheet>
  );
}
